import { useCallback, useEffect, useState } from "react";
import { User, Status } from "../data/FrontendTypes";

type Filter = {
    active: boolean;
    name: string;
    condition: (user: User) => boolean;
}
type UserListProps = {
    users: User[];
};

const UserList: React.FC<UserListProps> = ({ users }) => {

    const [filteredList, setFilteredList] = useState<User[]>([...users])
    const [filter, setFilter] = useState<string|undefined>(undefined)

    const [sortCriteria, setSortCriteria] = useState<"name"|"email"|"status">("name");
    const [sortOrder, setSortOrder] = useState<"asc"|"desc">("asc");

    const sortUsers = useCallback((users: User[]): User[] => {
        if (!sortCriteria) return users;
    
        const sortedUsers = [...users].sort((a, b) => {
            if (a[sortCriteria] < b[sortCriteria]) return sortOrder === 'asc' ? -1 : 1;
            if (a[sortCriteria] > b[sortCriteria]) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
    
        return sortedUsers;
    }, [sortCriteria, sortOrder]);

    const containsText = useCallback((user: User): boolean => {
        if (filter) {
            return user.email.toLowerCase().includes(filter.toLowerCase()) || user.name.toLowerCase().includes(filter.toLowerCase())
        } else return true;
    }, [filter]);

    const [allFilters, setAllFilters] = useState<Filter[]>([
        {
            active: false,
            name: "Aktuell",
            condition: (user: User) => {
                const statusValue = typeof user.status === 'string' ? Status[user.status as keyof typeof Status] : user.status;
                return statusValue === Status.UP_TO_DATE;
            }
        },
        {
            active: false,
            name: "Ohne",
            condition: (user: User) => {
                const statusValue = typeof user.status === 'string' ? Status[user.status as keyof typeof Status] : user.status;
                return statusValue === Status.EMPTY;
            }
        },
        {
            active: false,
            name: "Alt",
            condition: (user: User) => {
                const statusValue = typeof user.status === 'string' ? Status[user.status as keyof typeof Status] : user.status;
                return statusValue === Status.OLD;
            }
        }
    ])

    function toggleFilter(index: number): void {
        const newFilters = [...allFilters];
        newFilters[index].active = !newFilters[index].active;;
        setAllFilters(newFilters);
    }

    useEffect(() => {
        let newList = users;
        
        // Apply text filter
        if (filter) {
            newList = newList.filter((user) => containsText(user));  
        } 
        
        // Apply status filters
        if (allFilters.some((filter) => filter.active)) {
            newList = newList.filter((user) => 
                allFilters
                    .filter((filter) => filter.active)
                    .some((filter) => filter.condition(user))
            );
        }
        
        newList = sortUsers(newList);
        setFilteredList(newList);
    }, [filter, users, allFilters, containsText, sortUsers]);

    const handleSort = (criteria: "name"|"email"|"status") => {
        if (criteria === sortCriteria) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortCriteria(criteria);
            setSortOrder('asc');
        }
    }

    return (
        <div className="user-list">
            <input type="text" name="Filter" id="" placeholder='Filter' value={filter} onChange={(e) => setFilter(e.target.value)}/>
            <div className="filters">
                { allFilters.map((filter, index) => (
                    <button 
                        className={`filter ${filter.active ? 'active' : ''}`} 
                        key={index}
                        onClick={() => toggleFilter(index)}    
                    >
                        {filter.name}
                    </button>
                ))}
            </div>
            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th>
                                <div
                                onClick={() => handleSort("name")}
                                className={sortCriteria === 'name' ? sortOrder : ''}
                                >
                                    Name
                                </div>
                            </th>
                            <th>
                                <div
                                onClick={() => handleSort("email")}
                                className={sortCriteria === 'email' ? sortOrder : ''}
                                >
                                    E-Mail
                                </div>
                            </th>
                            <th>
                                <div
                                onClick={() => handleSort("status")}
                                className={sortCriteria === 'status' ? sortOrder : ''}
                                >
                                    Status
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        { filteredList.map((user, index) => (
                            <UserRow key={index} user={user} />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

}

const UserRow: React.FC<{ user: User }> = ({ user }) => {
    return (
        <tr>
            <td>{user.name}</td>
            <td>{user.email}</td>
            <UserStatus status={user.status}/>
        </tr>
    );
}

const UserStatus: React.FC<{ status: Status | string }> = ({ status }) => {
    const statusValue = typeof status === 'string' ? Status[status as keyof typeof Status] : status;
    
    switch (statusValue) {
        case Status.EMPTY:
            return <td className='status empty'>Ohne</td>
        case Status.OLD:
            return <td className='status old'>Alt</td>
        case Status.UP_TO_DATE:
            return <td className='status up-to-date'>Aktuell</td>
        default:
            return <td className='status unknown'>Unbekannt</td>
    }
}

export default UserList;